import { layoutNodes } from "@/app/(v2)/workflows/_hooks/useLayout";
import type { ActionTriggerDefinition } from "@/hooks/useActionTriggerDefinitions";
import { getWorkflowVerticalGap } from "@/lib/workflow/layoutPolicy";
import type { StreamingNode } from "@/types/aiChat";
import type { Edge, Node } from "@xyflow/react";

import { getCompactPreference } from "@/lib/workflow/compactPreference";
import { STREAMING_X_CENTER } from "./workflowTransform";

/**
 * Add a streaming node to the React Flow canvas progressively.
 * This function is called as nodes stream in from the AI.
 *
 * Uses functional setState to ensure we always have the latest nodes array.
 *
 * EDGE ID CONSISTENCY (Critical for avoiding duplicate children bug):
 * Edges are created during streaming using the "=>" separator (e.g., "parent=>child").
 * This MUST match the format used in refineCanvasWithFinalWorkflow() so that edges
 * get REPLACED (not duplicated) when refinement occurs.
 *
 * Previously, streaming used "->" separator while refinement used "=>", causing
 * different edge IDs for the same connection. This resulted in both edge sets
 * existing in state, which caused duplicate children when the workflow was saved
 * (since buildApiNodes derives children from edges).
 *
 * @param clearFirst - If true, clears all existing nodes before adding the new one.
 *                     This ensures clearing and adding happen atomically.
 */
export function addStreamingNodeToCanvas(
  streamingNode: StreamingNode,
  setNodes: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void,
  setEdges: (edges: Edge[] | ((edges: Edge[]) => Edge[])) => void,
  definitions?: ActionTriggerDefinition[],
  clearFirst?: boolean,
): void {
  // Find definition for enrichment
  const definition = definitions?.find(d => d.type === streamingNode.type);

  // Determine node type: trigger for root, logic for if/for-each/etc, action otherwise.
  // Using the correct type ensures the right handles render (e.g., source-then/source-else for if nodes).
  const isRoot = streamingNode.id === "root";
  let nodeType: string = "action";
  if (isRoot) {
    nodeType = "trigger";
  } else if (definition?.category === "logic") {
    nodeType = "logic";
  }

  // If clearing first, clear edges too
  if (clearFirst) {
    setEdges([]);
  }

  // Edges to create from existing parents that already listed this node as a child.
  // Populated inside setNodes (which has access to current nodes), consumed in setEdges.
  // This is needed because if-else nodes have no default bottom handle — edges
  // must specify sourceHandle ("source-then"/"source-else") to render.
  const parentEdges: Array<{ source: string; sourceHandle?: string }> = [];

  // Use functional update to get the current nodes array
  setNodes((currentNodes: Node[]) => {
    // If clearFirst is true, start with empty array (atomic clear + add)
    const baseNodes = clearFirst ? [] : currentNodes;

    // Check if node already exists (only relevant if not clearing)
    if (!clearFirst && baseNodes.some(n => n.id === streamingNode.id)) {
      return baseNodes;
    }

    // Remove any placeholder nodes
    const filteredNodes = baseNodes.filter(n => n.type !== "placeholder");

    // Check if any existing node already has this new node in its children.
    // This creates "reverse" edges (parent→this) with correct sourceHandle.
    const waitNodesWithBranches = new Set<string>();
    for (const node of filteredNodes) {
      const nodeChildren = node.data?.children as string[] | undefined;
      if (nodeChildren?.includes(streamingNode.id)) {
        const isIfNode = node.data?.type === "if";
        const isWaitNode = node.data?.type === "wait";
        let sourceHandle: string | undefined;
        if (isIfNode) {
          sourceHandle = streamingNode.branch === "else" ? "source-else" : "source-then";
        } else if (isWaitNode) {
          // Tag the parent wait node so LogicNode renders side handles.
          if (streamingNode.branch === "resumed" || streamingNode.branch === "timed_out") {
            waitNodesWithBranches.add(node.id);
          }
          // Don't assign sourceHandle for wait-node branching edges during
          // streaming. The LogicNode needs a re-render to add the
          // source-resumed/source-timed_out handles, but React Flow
          // validates edges before the DOM updates. The done event
          // rebuilds all edges with correct sourceHandles.
        }
        parentEdges.push({
          source: node.id,
          sourceHandle,
        });
      }
    }

    // Create the React Flow node with a temporary position (layoutNodes will reposition)
    const newNode: Node = {
      id: streamingNode.id,
      type: nodeType,
      position: { x: STREAMING_X_CENTER, y: 0 },
      draggable: true,
      data: {
        type: streamingNode.type,
        name: definition?.name || streamingNode.type,
        description: "", // Intentionally empty — AI descriptions arrive async
        platform: definition?.platform || "",
        payload: streamingNode.payload || {},
        connector: streamingNode.connector,
        branch: streamingNode.branch,
        isStreaming: true, // Mark as streaming for visual feedback
        children: streamingNode.children, // Store children for edge creation
      },
    };

    // Apply hasWaitBranches immutably to avoid mutating React state references
    const allNodes = [...filteredNodes, newNode].map(n =>
      waitNodesWithBranches.has(n.id) ? { ...n, data: { ...n.data, hasWaitBranches: true } } : n,
    );

    // Build edge list from all nodes' children + loopBody data for layout calculation
    const edges: Edge[] = [];
    const edgeIds = new Set<string>();
    for (const node of allNodes) {
      for (const key of ["children", "loopBody"] as const) {
        const targets = node.data?.[key] as string[] | undefined;
        if (targets) {
          for (const childId of targets) {
            const edgeId = `${node.id}=>${childId}`;
            if (!edgeIds.has(edgeId)) {
              edgeIds.add(edgeId);
              edges.push({ id: edgeId, source: node.id, target: childId, type: "workflow" });
            }
          }
        }
      }
    }

    // Run layoutNodes for proper tree positioning (respects compact preference)
    const isCompact = getCompactPreference();
    const layouted = layoutNodes(allNodes, edges, { verticalGap: getWorkflowVerticalGap(isCompact) });

    // If layout succeeded, use it; otherwise fall back to the temporary positions
    return layouted.length > 0 ? layouted : allNodes;
  });

  // Build edges using the same ID format as refineCanvasWithFinalWorkflow ("=>")
  // to avoid duplicates when refinement occurs.
  setEdges((currentEdges: Edge[]) => {
    let updatedEdges = currentEdges;
    const newEdges: Edge[] = [];
    const existingEdgeIds = new Set(currentEdges.map(e => e.id));

    // Forward edges: this node → its children.
    if (streamingNode.children?.length) {
      for (const childId of streamingNode.children) {
        const edgeId = `${streamingNode.id}=>${childId}`;
        if (!existingEdgeIds.has(edgeId)) {
          newEdges.push({
            id: edgeId,
            source: streamingNode.id,
            target: childId,
            type: "workflow",
          });
          existingEdgeIds.add(edgeId);
        }
      }
    }

    // Reverse edges: existing parent → this node (with correct sourceHandle).
    // REPLACE any existing forward edge from the same parent, because it may
    // lack sourceHandle (e.g., if-else nodes need "source-then"/"source-else"
    // for the edge to connect to the correct handle).
    const isThisNodeForEach = definition?.category === "logic" && streamingNode.type === "for-each";
    for (const parent of parentEdges) {
      const edgeId = `${parent.source}=>${streamingNode.id}`;

      // Remove existing edge (may lack sourceHandle) — from both sources
      updatedEdges = updatedEdges.filter(e => e.id !== edgeId);
      const newIdx = newEdges.findIndex(e => e.id === edgeId);
      if (newIdx >= 0) newEdges.splice(newIdx, 1);

      newEdges.push({
        id: edgeId,
        source: parent.source,
        target: streamingNode.id,
        type: "workflow",
        ...(parent.sourceHandle && { sourceHandle: parent.sourceHandle }),
        ...(isThisNodeForEach && { data: { isTargetForEach: true } }),
      });
    }

    if (newEdges.length === 0 && updatedEdges === currentEdges) {
      return currentEdges;
    }

    return [...updatedEdges, ...newEdges];
  });
}

/**
 * Clear streaming markers from all nodes (call when streaming completes)
 */
export function clearStreamingMarkers(setNodes: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void): void {
  setNodes((nodes: Node[]) =>
    nodes.map(n => ({
      ...n,
      data: { ...n.data, isStreaming: false },
    })),
  );
}

/**
 * Update an existing node in-place during streaming (for edit mode).
 * This preserves the node's position while updating its data/payload.
 *
 * @param streamingNode - The streaming node data from AI
 * @param setNodes - React Flow setNodes function
 * @param definitions - Action/trigger definitions for enrichment
 * @returns true if node was updated, false if node doesn't exist (needs to be added)
 */
export function updateStreamingNodeInPlace(
  streamingNode: StreamingNode,
  setNodes: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void,
  definitions?: ActionTriggerDefinition[],
): boolean {
  let nodeExists = false;

  // Find definition for enrichment
  const definition = definitions?.find(d => d.type === streamingNode.type);

  setNodes((currentNodes: Node[]) => {
    // Check if node exists
    const existingNodeIndex = currentNodes.findIndex(n => n.id === streamingNode.id);

    if (existingNodeIndex === -1) {
      // Node doesn't exist, return unchanged
      return currentNodes;
    }

    nodeExists = true;
    const existingNode = currentNodes[existingNodeIndex];

    // Generate refresh key to trigger AI highlight effect
    const refreshKey = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Determine correct React Flow node type in case it changed
    const isRoot = streamingNode.id === "root";
    let nodeType = existingNode.type;
    if (isRoot) {
      nodeType = "trigger";
    } else if (definition?.category === "logic") {
      nodeType = "logic";
    } else if (definition?.category === "action") {
      nodeType = "action";
    }

    // Update the node in-place, preserving position
    const updatedNode: Node = {
      ...existingNode,
      type: nodeType,
      // Keep position unchanged!
      data: {
        ...existingNode.data,
        type: streamingNode.type,
        name: definition?.name || streamingNode.type,
        description: "", // Intentionally empty — AI descriptions arrive async
        platform: definition?.platform || "",
        payload: streamingNode.payload || {},
        connector: streamingNode.connector || existingNode.data?.connector,
        branch: streamingNode.branch,
        children: streamingNode.children,
        isStreaming: true, // Mark as streaming for visual feedback
        _refreshKey: refreshKey, // Trigger AI highlight effect
      },
    };

    // Return new array with updated node
    const newNodes = [...currentNodes];
    newNodes[existingNodeIndex] = updatedNode;
    return newNodes;
  });

  return nodeExists;
}
